import * as Dialog from '@radix-ui/react-dialog';
import { Copy, KeyRound, UserRoundPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { useCreatePortalLogin } from '@/hooks/clients';
import { ApiError } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { ClientDetail, CreatedClientPortalLogin } from '@shared/schemas/client';

/*
 * The client's portal account, on the admin's Overview tab — ROADMAP.md §5.1.
 *
 * This closes a real gap rather than adding a convenience. Employees get a login
 * at the moment they are hired (3.4); nothing created one for a client, so
 * `client.user_id` stayed null and the portal had no record behind it. There is
 * no public sign-up (2.1) and there is not going to be, so an admin doing this
 * deliberately is the only way a customer ever gets in.
 */

export function PortalLoginCard({ detail }: { detail: ClientDetail }) {
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedClientPortalLogin | null>(null);

  const login = detail.portalLogin;

  return (
    <div className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-h3 text-ink-900">
            <KeyRound aria-hidden="true" className="size-[18px] text-bark-700" />
            Portal login
          </p>

          {login ? (
            <dl className="mt-2 space-y-1 text-body text-ink-700">
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-ink-500">Signs in with</dt>
                <dd className="text-ink-900">{login.email ?? login.username}</dd>
              </div>
              {login.email === null ? (
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-ink-500">Recovery email</dt>
                  <dd className="text-ink-900">
                    {login.recoveryEmail ?? (
                      <span className="italic text-ink-500">
                        Not set — they can never reset their own password
                      </span>
                    )}
                  </dd>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-ink-500">Last signed in</dt>
                {/* "Never" is an answer. A dash is not — P1. */}
                <dd className="text-ink-900">
                  {login.lastLoginAt ? (
                    formatInstant(login.lastLoginAt)
                  ) : (
                    <span className="italic text-ink-500">
                      Never — the account is unused so far
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="mt-2 max-w-prose text-body text-ink-500">
              This client cannot sign in to the portal yet. Creating a login lets them see their
              cleaning history and photos, and ask for a clean without ringing you.
            </p>
          )}
        </div>

        {login ? null : (
          <Button
            className="shrink-0"
            onClick={() => {
              setCreating(true);
            }}
          >
            <UserRoundPlus aria-hidden="true" />
            Create portal login
          </Button>
        )}
      </div>

      <CreateLoginDialog
        detail={detail}
        open={creating}
        onOpenChange={setCreating}
        onCreated={(result) => {
          setCreating(false);
          setCreated(result);
        }}
      />

      <PasswordOnceDialog
        created={created}
        onClose={() => {
          setCreated(null);
        }}
      />
    </div>
  );
}

function CreateLoginDialog({
  detail,
  open,
  onOpenChange,
  onCreated,
}: {
  detail: ClientDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (created: CreatedClientPortalLogin) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <CreateLoginForm detail={detail} onCreated={onCreated} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CreateLoginForm({
  detail,
  onCreated,
}: {
  detail: ClientDetail;
  onCreated: (created: CreatedClientPortalLogin) => void;
}) {
  const create = useCreatePortalLogin(detail.id);

  // Defaults to the address already on the client record, because that is
  // almost always the answer and re-typing it is a chance to get it wrong.
  const [email, setEmail] = useState(detail.email ?? '');
  const [username, setUsername] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');

  const error = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    create.mutate(
      {
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(username.trim() ? { username: username.trim() } : {}),
        ...(recoveryEmail.trim() ? { recoveryEmail: recoveryEmail.trim() } : {}),
      },
      { onSuccess: onCreated },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">Create a portal login</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        For {detail.displayName}. We generate the password and show it once — you pass it on.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <Field
          label="Email address"
          name="email"
          type="email"
          value={email}
          error={fieldErrors.email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          hint="What they type to sign in. Leave blank only if they have no email at all."
        />

        <Field
          label="Or a username"
          name="username"
          value={username}
          error={fieldErrors.username}
          onChange={(event) => {
            setUsername(event.target.value);
          }}
          placeholder="dana.homeowner"
          hint="Letters, numbers, dots and underscores."
        />

        {username.trim() && !email.trim() ? (
          <Field
            label="Recovery email"
            name="recoveryEmail"
            type="email"
            required
            value={recoveryEmail}
            error={fieldErrors.recoveryEmail}
            onChange={(event) => {
              setRecoveryEmail(event.target.value);
            }}
            // P9, stated where somebody is about to create the exact account it
            // is about.
            hint="A username account has no email, so a password reset has nowhere to go without this. Without one they can never get back in."
          />
        ) : null}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={create.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create login'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * The password, shown once, on a screen that says so.
 *
 * Nothing stores it in readable form — not the database, not the log, not the
 * query cache. So this dialog is genuinely the only place it will ever exist,
 * and the copy has to make that unmissable rather than polite.
 */
function PasswordOnceDialog({
  created,
  onClose,
}: {
  created: CreatedClientPortalLogin | null;
  onClose: () => void;
}) {
  const toast = useToast();

  return (
    <Dialog.Root
      open={created !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <Dialog.Title className="text-h2 text-ink-900">Portal login created</Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            Copy this password now. It is not stored anywhere and cannot be shown again — if it is
            lost, the way back in is a password reset.
          </Dialog.Description>

          {created ? (
            <>
              <dl className="mt-5 space-y-3">
                <div>
                  <dt className="text-meta text-ink-500">Signs in with</dt>
                  <dd className="mt-0.5 text-body text-ink-900">
                    {created.login.email ?? created.login.username}
                  </dd>
                </div>
                <div>
                  <dt className="text-meta text-ink-500">Temporary password</dt>
                  <dd className="mt-1 flex items-center gap-2">
                    <code className="min-w-0 flex-1 break-all rounded-sm border border-ink-300 bg-ink-100/50 px-3 py-2 text-body text-ink-900">
                      {created.temporaryPassword}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="Copy password"
                      onClick={() => {
                        void navigator.clipboard.writeText(created.temporaryPassword).then(
                          () => {
                            toast.success('Password copied.');
                          },
                          () => {
                            // A clipboard write can be refused — an insecure
                            // origin, a permission. Saying so beats a button
                            // that silently does nothing.
                            toast.error(
                              'Could not copy — select the password and copy it by hand.',
                            );
                          },
                        );
                      }}
                    >
                      <Copy aria-hidden="true" />
                    </Button>
                  </dd>
                </div>
              </dl>

              {/*
                **This string has now gone stale twice** — it said "that arrives
                in phase 8" until phase 8 shipped, then said reset emails were
                not reaching inboxes after outbound mail was solved on
                2026-08-05. Both rewrites landed on the same rule: state only
                what is true of the running system, with no milestone and no
                "yet", so there is nothing here to expire.
              */}
              <p className="mt-4 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
                Tell them to change it after their first sign-in. If they lose it, “Forgot password”
                on the sign-in page emails them a reset link, or you can create a new one from here.
              </p>
            </>
          ) : null}

          <div className="mt-5 flex justify-end">
            <Dialog.Close asChild>
              <Button>I have copied it</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
